/** App-wide timezone: Seattle (Pacific). */
export const APP_TIMEZONE = 'America/Los_Angeles';

function getZonedParts(date, timeZone = APP_TIMEZONE) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    timeZoneName: 'longOffset',
  });
  const parts = Object.fromEntries(dtf.formatToParts(date).map((p) => [p.type, p.value]));
  let offset = String(parts.timeZoneName || 'GMT').replace(/^GMT/i, '').replace(/^UTC/i, '');
  if (!offset) offset = '+00:00';
  if (/^[+-]\d{2}$/.test(offset)) offset = `${offset}:00`;
  if (offset === 'Z') offset = '+00:00';
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour,
    minute: parts.minute,
    second: parts.second,
    offset,
  };
}

/** Parse YYYY-MM-DD (or ISO date prefix) as a calendar date (no TZ shift). */
export function parseLocalDate(value) {
  if (!value) return null;
  const dateOnly = String(value).slice(0, 10);
  const [y, m, d] = dateOnly.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** Calendar date YYYY-MM-DD in Seattle. */
export function toAppDateString(date = new Date()) {
  const p = getZonedParts(date instanceof Date ? date : new Date(date));
  return `${p.year}-${p.month}-${p.day}`;
}

export function todayInAppTz(date = new Date()) {
  return toAppDateString(date);
}

/**
 * Parse DB/API datetimes as UTC when they are naive SQLite strings
 * (YYYY-MM-DD HH:MM:SS / YYYY-MM-DDTHH:MM:SS without offset).
 * ISO strings with Z/offset parse normally.
 */
export function parseAppTimestamp(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  // Milliseconds since epoch (e.g. from Date#getTime())
  if (typeof value === 'number' && Number.isFinite(value)) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const str = String(value).trim();
  if (!str) return null;

  // Numeric string of epoch ms
  if (/^\d{11,15}$/.test(str)) {
    const date = new Date(Number(str));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const sqliteFormat = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/;
  const match = str.match(sqliteFormat);
  if (match) {
    const [, year, month, day, hour, minute, second] = match;
    const date = new Date(
      Date.UTC(
        parseInt(year, 10),
        parseInt(month, 10) - 1,
        parseInt(day, 10),
        parseInt(hour, 10),
        parseInt(minute, 10),
        parseInt(second || '0', 10)
      )
    );
    return Number.isNaN(date.getTime()) ? null : date;
  }

  // Date-only → treat as Seattle calendar day at noon to avoid DST edge flips in display
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    return seattleWallToUtcDate(str, '12:00:00');
  }

  const parsed = new Date(str);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Convert Seattle wall-clock date+time to a UTC Date. */
export function seattleWallToUtcDate(dateStr, timeStr = '00:00:00') {
  const [y, mo, d] = String(dateStr).split('-').map(Number);
  const timeParts = String(timeStr).split(':').map(Number);
  const h = timeParts[0] || 0;
  const mi = timeParts[1] || 0;
  const s = timeParts[2] || 0;
  let utcMs = Date.UTC(y, mo - 1, d, h, mi, s);
  for (let i = 0; i < 4; i++) {
    const seen = getZonedParts(new Date(utcMs));
    const seenUtc = Date.UTC(
      Number(seen.year),
      Number(seen.month) - 1,
      Number(seen.day),
      Number(seen.hour),
      Number(seen.minute),
      Number(seen.second)
    );
    const wantUtc = Date.UTC(y, mo - 1, d, h, mi, s);
    const delta = wantUtc - seenUtc;
    if (delta === 0) break;
    utcMs += delta;
  }
  return new Date(utcMs);
}

/**
 * True when the shift's end wall-clock time is at or before `now` in Seattle.
 * Overnight (end <= start) is not supported — those shifts are invalid elsewhere.
 */
export function scheduleShiftHasEnded({ shift_date, end_time } = {}, now = new Date()) {
  const date = String(shift_date || "").slice(0, 10);
  const end = String(end_time || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !end) return false;
  const time = end.length === 5 ? `${end}:00` : end;
  const endAt = seattleWallToUtcDate(date, time);
  if (Number.isNaN(endAt.getTime())) return false;
  return endAt.getTime() <= now.getTime();
}

/** Seattle wall-clock parts from an instant (ISO/Date). */
export function toAppWallParts(value) {
  const date = value instanceof Date ? value : parseAppTimestamp(value) || new Date(value);
  if (!date || Number.isNaN(date.getTime())) return null;
  const p = getZonedParts(date);
  return {
    date: `${p.year}-${p.month}-${p.day}`,
    time: `${p.hour}:${p.minute}`,
    timeWithSeconds: `${p.hour}:${p.minute}:${p.second}`,
  };
}

export function formatInAppTz(value, options = {}) {
  const date = value instanceof Date ? value : parseAppTimestamp(value);
  if (!date || Number.isNaN(date.getTime())) return '';
  const hasStyle = options.dateStyle != null || options.timeStyle != null;
  const hasParts =
    options.year != null ||
    options.month != null ||
    options.day != null ||
    options.hour != null ||
    options.minute != null ||
    options.second != null;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: APP_TIMEZONE,
    ...(hasStyle || hasParts
      ? options
      : {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          ...options,
        }),
  }).format(date);
}

/** e.g. Jul 24, 2026 10:38 PM */
export function formatAppDateTime(value) {
  return formatInAppTz(value, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** e.g. Jul 24, 2026 */
export function formatAppDate(value) {
  return formatInAppTz(value, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** e.g. 10:38 PM */
export function formatAppTime(value) {
  return formatInAppTz(value, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Format an instant that may be ISO/SQLite or an already Seattle-formatted string
 * (e.g. audit change_details written by the backend).
 */
export function formatDetailInstant(value, { timeOnly = false } = {}) {
  if (value == null || value === '') return '';
  const str = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2})/.test(str) || /[zZ]|[+-]\d{2}:?\d{2}$/.test(str)) {
    return timeOnly ? formatAppTime(str) : formatInAppTz(str);
  }
  if (timeOnly) {
    const m = str.match(/(\d{1,2}:\d{2}(?::\d{2})?\s*[AP]M)/i);
    return m ? m[1] : str;
  }
  return str;
}
